/**
 * Flight operations · flight data feeds: the live channel from an airline's
 * or an airport's own systems — SITA, Amadeus — into the flight record.
 *
 * What an operations room needs from it, in the order it needs it:
 *
 *  - **Is it working?** Each feed is live, quiet or failing, with the lag
 *    since its last message and why, so a silent feed is noticed before the
 *    board goes stale rather than after.
 *  - **What did it do?** Every message as it arrived, and what became of it —
 *    taken, held (not on the record yet, or older than what is there), stored
 *    for a person, a duplicate, or refused with the reason — and a held
 *    message can be replayed once its flight is on the record.
 *  - **How is it set up?** Where the source posts or where we fetch from, how
 *    it signs in, and which secret it uses — by name, set or not set. A
 *    credential never appears here: the platform refuses to keep one.
 */
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import {
  useFlightFeedMessages,
  useFlightFeeds,
  useReplayFeedMessage,
  useSaveFlightFeed,
  useTestFlightFeed,
} from "../../api/queries";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Select,
  Skeleton,
} from "../../design/components";
import {
  KIND_LABEL,
  OUTCOMES,
  OUTCOME_LABEL,
  STATE_LABEL,
  TRANSPORT_LABEL,
  type FeedDraft,
  type FeedKind,
  type FeedMessage,
  type FeedOutcome,
  type FeedResult,
  type FeedTransport,
  type FlightFeed,
} from "../../domain/flightFeeds";
import { t, tc } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { hhmm as clock } from "../../lib/format";
import { FlightTheme } from "./FlightTheme";
import s from "./Flights.module.css";
import a from "./Aodb.module.css";
import f from "./FlightFeeds.module.css";

type Tab = "feeds" | "log" | "configure";
const hhmm = (iso?: string) => clock(iso, "—");

/** The lag as a person reads it. */
function lagText(seconds?: number): string {
  if (seconds == null) return "—";
  const m = Math.round(seconds / 60);
  if (m < 1) return t("under a minute");
  if (m < 60) return t("{m} min ago", { m });
  const h = Math.floor(m / 60);
  if (h < 48) return t("{h} h {m} min ago", { h, m: m % 60 });
  return t("{d} days ago", { d: Math.floor(h / 24) });
}

export function FlightFeedsPage() {
  const session = useSession();
  const [tab, setTab] = useState<Tab>("feeds");
  const [editing, setEditing] = useState<FlightFeed | null>(null);
  const [logFeed, setLogFeed] = useState<string>("");
  const feeds = useFlightFeeds();
  const items = feeds.data?.items ?? [];
  const failing = items.filter((x) => x.status.state === "failing");
  const quiet = items.filter((x) => x.status.state === "quiet");

  useScreenContext({
    route: "/flight-feeds",
    title: "Flight data feeds",
    summary: feeds.data
      ? [
          t("{n} feeds: {l} live, {q} quiet, {f} failing.", {
            n: items.length,
            l: items.filter((x) => x.status.state === "live").length,
            q: quiet.length,
            f: failing.length,
          }),
          ...failing.map((x) => t("{name} is failing: {why}", { name: x.name, why: x.status.why ?? "" })),
        ]
      : [],
    offers: [{ label: t("Which feeds are failing?"), ask: "which flight data feeds are failing or quiet, and why?" }],
    worksInFull: true,
  });

  return (
    <FlightTheme>
      <Page eyebrow={t("{site} · Flight operations", { site: session.site.code })} title="Flight data feeds">
        <p className={s.lede}>
          {t(
            "Live flight messages from airline and airport systems, such as MVT, LDM, and AIDX.",
          )}
        </p>
        {feeds.isPending && <Skeleton rows={6} />}
        {feeds.isError && <ErrorState error={feeds.error} retry={() => void feeds.refetch()} />}
        {feeds.data?.sample && (
          <p className={f.sample} data-testid="feeds-sample">
            {t("Sample feeds — no airline or airport system is connected. Configure a feed to connect one.")}
          </p>
        )}
        {feeds.data && (
          <>
            <Segmented
              ariaLabel="Flight data feeds"
              value={tab}
              onChange={setTab}
              options={[
                { value: "feeds", label: "Feed status" },
                { value: "log", label: "Message log" },
                { value: "configure", label: "Configure" },
              ]}
            />
            {tab === "feeds" && (
              <FeedList
                items={items}
                onEdit={(x) => {
                  setEditing(x);
                  setTab("configure");
                }}
                onLog={(x) => {
                  setLogFeed(x.id);
                  setTab("log");
                }}
              />
            )}
            {tab === "log" && <MessageLog feeds={items} feedId={logFeed} setFeedId={setLogFeed} />}
            {tab === "configure" && (
              <ConfigureFeed key={editing?.id ?? "new"} feed={editing} onDone={() => setEditing(null)} />
            )}
          </>
        )}
      </Page>
    </FlightTheme>
  );
}

function StateBadge({ feed }: { feed: FlightFeed }) {
  return (
    <span className={f.state} data-state={feed.status.state} data-testid={`feed-state-${feed.key}`}>
      {tc(STATE_LABEL[feed.status.state], "feed")}
    </span>
  );
}

function FeedList({
  items,
  onEdit,
  onLog,
}: {
  items: FlightFeed[];
  onEdit: (f: FlightFeed) => void;
  onLog: (f: FlightFeed) => void;
}) {
  if (!items.length)
    return (
      <EmptyState>
        {t(
          "No flight feed is set up yet. Add one for each system that sends flight messages.",
        )}
      </EmptyState>
    );
  return (
    <div className={f.list}>
      {items.map((x) => (
        <Card
          key={x.id}
          testId={`feed-${x.key}`}
          title={
            <span className={f.head}>
              <StateBadge feed={x} /> {x.name}
            </span>
          }
          sub={`${t(KIND_LABEL[x.kind])} · ${t(TRANSPORT_LABEL[x.transport])}`}
          actions={
            <>
              <Button size="sm" onClick={() => onLog(x)}>
                {t("Messages")}
              </Button>
              <Button size="sm" onClick={() => onEdit(x)}>
                {t("Settings")}
              </Button>
            </>
          }
        >
          {x.status.why && (
            <p className={f.why} data-state={x.status.state}>
              {x.status.why}
            </p>
          )}
          <dl className={a.kv}>
            <dt>{t("last message")}</dt>
            <dd>
              {x.status.lastMessageAt
                ? `${hhmm(x.status.lastMessageAt)} · ${lagText(x.status.lagSeconds)}`
                : t("none yet")}
            </dd>
            <dt>{t("messages in the last hour")}</dt>
            <dd>{x.status.perHour}</dd>
            {x.status.sourceLagSeconds != null && (
              <>
                <dt>{t("behind the source")}</dt>
                <dd>{t("{n} s on average", { n: Math.round(x.status.sourceLagSeconds) })}</dd>
              </>
            )}
            <dt>{t("where it comes from")}</dt>
            <dd className={f.wrap}>
              {x.transport === "push"
                ? x.pushPath
                : x.transport === "imap"
                  ? `${x.config.imap?.user ?? ""} @ ${x.config.imap?.host ?? ""}`
                  : x.config.url}
            </dd>
            {Object.entries(x.secrets).map(([k, v]) => (
              <SecretRow key={k} role={k} name={v.name} set={v.set} />
            ))}
          </dl>
          <div className={f.outcomes} data-testid={`feed-outcomes-${x.key}`}>
            {OUTCOMES.map((o) => (
              <span key={o} className={f.outcome} data-o={o}>
                <b>{x.status.outcomes24h[o] ?? 0}</b> {tc(OUTCOME_LABEL[o], "feed")}
              </span>
            ))}
            <span className={a.dim}>{t("in 24 hours")}</span>
          </div>
          {x.status.refusals24h.length > 0 && (
            <div className={f.refusals} data-testid={`feed-refusals-${x.key}`}>
              <div className={a.h3}>{t("Refused, by reason")}</div>
              <ul>
                {x.status.refusals24h.map((r) => (
                  <li key={r.reason}>
                    <b>{r.count}</b> {r.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function SecretRow({ role, name, set }: { role: string; name: string; set: boolean }) {
  return (
    <>
      <dt>{t("secret: {role}", { role: role.replace(/_/g, " ") })}</dt>
      <dd>
        {name} <span className={set ? a.ok : a.flag}>{set ? t("set") : t("not set")}</span>
      </dd>
    </>
  );
}

function MessageLog({
  feeds,
  feedId,
  setFeedId,
}: {
  feeds: FlightFeed[];
  feedId: string;
  setFeedId: (v: string) => void;
}) {
  const [outcome, setOutcome] = useState<FeedOutcome | "">("");
  const [open, setOpen] = useState<string | null>(null);
  const log = useFlightFeedMessages({ feedId: feedId || undefined, outcome: outcome || undefined });
  const replay = useReplayFeedMessage();
  const [replayed, setReplayed] = useState<Record<string, FeedResult>>({});
  return (
    <Card title={t("Messages in")} sub={t("Newest first. Open one to see it as it arrived.")}>
      <div className={f.filters}>
        <Field label="Feed">
          <Select value={feedId} onChange={(e) => setFeedId(e.target.value)} data-testid="log-feed">
            <option value="">{t("every feed")}</option>
            {feeds.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Outcome">
          <Select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as FeedOutcome | "")}
            data-testid="log-outcome"
          >
            <option value="">{t("every outcome")}</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {tc(OUTCOME_LABEL[o], "feed")}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {log.isPending && <Skeleton rows={5} />}
      {log.isError && <ErrorState error={log.error} retry={() => void log.refetch()} />}
      {log.data && log.data.length === 0 && <EmptyState>{t("No messages match.")}</EmptyState>}
      {log.data && log.data.length > 0 && (
        <div className={a.scroll}>
          <table className={a.table} data-testid="feed-log">
            <thead>
              <tr>
                <th>{t("Received")}</th>
                <th>{t("Feed")}</th>
                <th>{t("Type")}</th>
                <th>{t("Outcome")}</th>
                <th>{t("Why, or what changed")}</th>
              </tr>
            </thead>
            <tbody>
              {log.data.map((m) => (
                <LogRow
                  key={m.id}
                  m={m}
                  open={open === m.id}
                  toggle={() => setOpen(open === m.id ? null : m.id)}
                  onReplay={() =>
                    replay.mutate(m.id, { onSuccess: (r) => setReplayed((p) => ({ ...p, [m.id]: r.results[0] })) })
                  }
                  replaying={replay.isPending && replay.variables === m.id}
                  replayed={replayed[m.id]}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function LogRow({
  m,
  open,
  toggle,
  onReplay,
  replaying,
  replayed,
}: {
  m: FeedMessage;
  open: boolean;
  toggle: () => void;
  onReplay: () => void;
  replaying: boolean;
  replayed?: FeedResult;
}) {
  return (
    <>
      <tr className={open ? a.on : undefined} onClick={toggle} data-testid={`log-row-${m.id}`}>
        <td className={a.t}>{hhmm(m.receivedAt)}</td>
        <td>{m.feed ?? t("posted directly")}</td>
        <td>
          <span className={a.src} data-src={m.kind === "aidx" ? "aidx" : m.kind === "mvt" ? "mvt" : undefined}>
            {m.kind.toUpperCase()}
          </span>
        </td>
        <td>
          <span className={f.outcome} data-o={m.outcome}>
            {tc(OUTCOME_LABEL[m.outcome], "feed")}
          </span>
        </td>
        <td className={f.reason}>{m.reason ?? (m.changes.length ? m.changes.join(", ") : "—")}</td>
      </tr>
      {open && (
        <tr className={f.detail}>
          <td colSpan={5}>
            <pre className={a.raw} data-testid="log-raw">
              {m.raw}
            </pre>
            {m.ignored.length > 0 && (
              <p className={a.dim}>
                {t("Not read (ignored and reported):")} {m.ignored.join(", ")}
              </p>
            )}
            {m.sentAt && (
              <p className={a.dim}>
                {t("Sent {sent}, received {received}.", { sent: hhmm(m.sentAt), received: hhmm(m.receivedAt) })}
              </p>
            )}
            {(m.outcome === "held" || m.outcome === "refused") && (
              <Button
                size="sm"
                disabled={replaying}
                onClick={(e) => {
                  e.stopPropagation();
                  onReplay();
                }}
                data-testid={`replay-${m.id}`}
              >
                {t("Replay this message")}
              </Button>
            )}
            {replayed && (
              <p className={f.result} data-o={replayed.outcome} data-testid="replay-result">
                {t("Replayed: {outcome}", { outcome: tc(OUTCOME_LABEL[replayed.outcome], "feed") })}
                {replayed.reason ? ` — ${replayed.reason}` : ""}
              </p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const SECRET_ROLES: Record<string, { transport: FeedTransport[]; auth?: string[] }> = {
  api_key: { transport: ["https"], auth: ["api_key"] },
  client_secret: { transport: ["https"], auth: ["oauth"] },
  password: { transport: ["imap"] },
};

function draftFrom(feed: FlightFeed | null): FeedDraft {
  if (feed)
    return {
      key: feed.key,
      name: feed.name,
      kind: feed.kind,
      transport: feed.transport,
      enabled: feed.enabled,
      config: structuredClone(feed.config),
    };
  return {
    key: "",
    name: "",
    kind: "aidx",
    transport: "push",
    enabled: true,
    config: { quietMinutes: 30, createUnknown: false, secretRefs: {} },
  };
}

function problems(d: FeedDraft, mappingText: string): string[] {
  const out: string[] = [];
  if (!/^[a-z0-9][a-z0-9_-]{1,39}$/.test(d.key))
    out.push(t("The key is 2–40 lower-case letters, digits, - or _; it goes in the push address."));
  if (!d.name.trim()) out.push(t("The feed needs a name."));
  if (d.transport === "imap" && d.kind !== "typeb") out.push(t("A mailbox carries Type B."));
  if (d.kind === "json" && d.transport !== "https") out.push(t("A JSON feed is fetched over HTTPS."));
  if (d.transport === "https" && !/^https?:\/\/[^/]+/.test(d.config.url ?? ""))
    out.push(t("A fetched feed needs its address (https://…)."));
  if (d.transport === "imap" && (!d.config.imap?.host || !d.config.imap?.user))
    out.push(t("A mailbox needs its host and user."));
  for (const v of Object.values(d.config.secretRefs ?? {}))
    if (v && !/^SLT_FEED_[A-Z0-9_]{1,60}$/.test(v))
      out.push(
        t("A secret is named, never typed: {v} is not an SLT_FEED_… variable name.", {
          v: v.length > 12 ? `${v.slice(0, 4)}…` : v,
        }),
      );
  if (d.kind === "json") {
    try {
      JSON.parse(mappingText || "{}");
    } catch {
      out.push(t("The field mapping is not valid JSON."));
    }
  }
  return out;
}

function ConfigureFeed({ feed, onDone }: { feed: FlightFeed | null; onDone: () => void }) {
  const [d, setD] = useState<FeedDraft>(() => draftFrom(feed));
  const [mappingText, setMappingText] = useState(() =>
    JSON.stringify(feed?.config.mapping ?? { records: "", id: "", cursor: "", fields: {} }, null, 2),
  );
  const [shown, setShown] = useState(false);
  const [sample, setSample] = useState("");
  const save = useSaveFlightFeed();
  const test = useTestFlightFeed();
  const set = (patch: Partial<FeedDraft>) => setD((x) => ({ ...x, ...patch }));
  const setC = (patch: Partial<FeedDraft["config"]>) => setD((x) => ({ ...x, config: { ...x.config, ...patch } }));
  const authKind = d.config.auth?.kind ?? "none";
  const roles = useMemo(
    () =>
      Object.entries(SECRET_ROLES)
        .filter(([, r]) => r.transport.includes(d.transport) && (!r.auth || r.auth.includes(authKind)))
        .map(([k]) => k),
    [d.transport, authKind],
  );
  const issues = problems(d, mappingText);
  const submit = () => {
    setShown(true);
    if (issues.length) return;
    const secretRefs = Object.fromEntries(
      Object.entries(d.config.secretRefs ?? {}).filter(([k, v]) => v && roles.includes(k)),
    );
    const config = {
      ...d.config,
      secretRefs,
      mapping: d.kind === "json" ? JSON.parse(mappingText || "{}") : undefined,
    };
    save.mutate({ draft: { ...d, config }, id: feed?.id }, { onSuccess: () => onDone() });
  };

  return (
    <div className={a.panel}>
      <Card title={feed ? t("Settings for {name}", { name: feed.name }) : t("A new feed")} testId="feed-form">
        <div className={a.form}>
          <Field label="Key (in the push address)">
            <Input
              value={d.key}
              disabled={!!feed}
              onChange={(e) => set({ key: e.target.value.trim().toLowerCase() })}
              data-testid="feed-key"
            />
          </Field>
          <Field label="Name">
            <Input value={d.name} onChange={(e) => set({ name: e.target.value })} data-testid="feed-name" />
          </Field>
          <Field label="What it sends">
            <Select
              value={d.kind}
              disabled={!!feed}
              onChange={(e) => set({ kind: e.target.value as FeedKind })}
              data-testid="feed-kind"
            >
              {(Object.keys(KIND_LABEL) as FeedKind[]).map((k) => (
                <option key={k} value={k}>
                  {t(KIND_LABEL[k])}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="How it arrives">
            <Select
              value={d.transport}
              disabled={!!feed}
              onChange={(e) => set({ transport: e.target.value as FeedTransport })}
              data-testid="feed-transport"
            >
              {(Object.keys(TRANSPORT_LABEL) as FeedTransport[]).map((k) => (
                <option key={k} value={k}>
                  {t(TRANSPORT_LABEL[k])}
                </option>
              ))}
            </Select>
          </Field>
          {d.transport === "https" && (
            <>
              <Field label="Address to fetch from">
                <Input
                  value={d.config.url ?? ""}
                  onChange={(e) => setC({ url: e.target.value.trim() })}
                  placeholder="https://"
                  data-testid="feed-url"
                />
              </Field>
              <Field label="Position parameter">
                <Input
                  value={d.config.cursorParam ?? "since"}
                  onChange={(e) => setC({ cursorParam: e.target.value.trim() })}
                />
              </Field>
              <Field label="Sign-in">
                <Select
                  value={authKind}
                  onChange={(e) =>
                    setC({ auth: { ...d.config.auth, kind: e.target.value as "none" | "api_key" | "oauth" } })
                  }
                  data-testid="feed-auth"
                >
                  <option value="none">{t("none")}</option>
                  <option value="api_key">{t("an API key in a header")}</option>
                  <option value="oauth">{t("OAuth client credentials")}</option>
                </Select>
              </Field>
              {authKind === "api_key" && (
                <Field label="Header">
                  <Input
                    value={d.config.auth?.header ?? "X-API-Key"}
                    onChange={(e) => setC({ auth: { ...d.config.auth!, header: e.target.value } })}
                  />
                </Field>
              )}
              {authKind === "oauth" && (
                <>
                  <Field label="Token address">
                    <Input
                      value={d.config.auth?.tokenUrl ?? ""}
                      onChange={(e) => setC({ auth: { ...d.config.auth!, tokenUrl: e.target.value.trim() } })}
                    />
                  </Field>
                  <Field label="Client id">
                    <Input
                      value={d.config.auth?.clientId ?? ""}
                      onChange={(e) => setC({ auth: { ...d.config.auth!, clientId: e.target.value.trim() } })}
                    />
                  </Field>
                </>
              )}
            </>
          )}
          {d.transport === "imap" && (
            <>
              <Field label="Mail server (IMAPS, port 993)">
                <Input
                  value={d.config.imap?.host ?? ""}
                  onChange={(e) => setC({ imap: { user: "", ...d.config.imap, host: e.target.value.trim() } })}
                  data-testid="feed-imap-host"
                />
              </Field>
              <Field label="Mailbox user">
                <Input
                  value={d.config.imap?.user ?? ""}
                  onChange={(e) => setC({ imap: { host: "", ...d.config.imap, user: e.target.value.trim() } })}
                />
              </Field>
              <Field label="Folder">
                <Input
                  value={d.config.imap?.folder ?? "INBOX"}
                  onChange={(e) => setC({ imap: { host: "", user: "", ...d.config.imap, folder: e.target.value } })}
                />
              </Field>
            </>
          )}
          {roles.map((role) => (
            <Field key={role} label={t("Secret name: {role}", { role: role.replace(/_/g, " ") })}>
              <Input
                value={d.config.secretRefs?.[role] ?? ""}
                placeholder="SLT_FEED_…"
                onChange={(e) =>
                  setC({ secretRefs: { ...d.config.secretRefs, [role]: e.target.value.trim().toUpperCase() } })
                }
                data-testid={`feed-secret-${role}`}
              />
            </Field>
          ))}
          {roles.length > 0 && (
            <p className={a.dim}>
              {t(
                "Name the environment variable that holds it (SLT_FEED_…), set from the deployment's secret manager. The secret itself is never typed here, kept or shown.",
              )}
            </p>
          )}
          {d.kind === "json" && (
            <Field label="Field mapping (JSON)">
              <textarea
                className={a.area}
                rows={8}
                value={mappingText}
                onChange={(e) => setMappingText(e.target.value)}
                data-testid="feed-mapping"
              />
            </Field>
          )}
          {d.transport !== "push" && (
            <Field label="Fetch every (seconds)">
              <Input
                type="number"
                min={30}
                value={d.config.pollSeconds ?? 60}
                onChange={(e) => setC({ pollSeconds: Number(e.target.value) })}
              />
            </Field>
          )}
          <Field label="Quiet after (minutes without a message)">
            <Input
              type="number"
              min={5}
              value={d.config.quietMinutes ?? 30}
              onChange={(e) => setC({ quietMinutes: Number(e.target.value) })}
            />
          </Field>
          <label className={f.check}>
            <input
              type="checkbox"
              checked={!!d.config.createUnknown}
              onChange={(e) => setC({ createUnknown: e.target.checked })}
            />{" "}
            {t(
              "May add flights the schedule does not have (charters, ad-hoc). Off, a flight not on the record is held.",
            )}
          </label>
          <label className={f.check}>
            <input
              type="checkbox"
              checked={d.enabled}
              onChange={(e) => set({ enabled: e.target.checked })}
              data-testid="feed-enabled"
            />{" "}
            {t("On")}
          </label>
          {shown && issues.length > 0 && (
            <ul className={f.issues} data-testid="feed-issues">
              {issues.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {save.isError && <p className={a.bad}>{(save.error as Error).message}</p>}
          <div className={f.buttons}>
            <Button variant="primary" onClick={submit} disabled={save.isPending} data-testid="feed-save">
              {feed ? t("Save the settings") : t("Add the feed")}
            </Button>
            {feed && <Button onClick={onDone}>{t("New feed instead")}</Button>}
          </div>
        </div>
      </Card>
      <Card
        title={t("Test it")}
        sub={t("A sample message, or one fetch from the source — read and checked, and nothing applied.")}
        testId="feed-test"
      >
        {!feed && <p className={a.dim}>{t("Save the feed first; then send it a sample here.")}</p>}
        {feed && (
          <div className={a.form}>
            <textarea
              className={a.area}
              rows={9}
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              placeholder={
                feed.kind === "aidx"
                  ? "<IATA_AIDX_FlightLegNotifRQ …>"
                  : feed.kind === "typeb"
                    ? "QU MEDKLSV\n.JEDKKSV 281200\nMVT\n…"
                    : "{ … }"
              }
              data-testid="feed-sample"
            />
            <Button
              onClick={() => test.mutate({ id: feed.id, sample: sample.trim() || undefined })}
              disabled={test.isPending}
              data-testid="feed-test-run"
            >
              {sample.trim()
                ? t("Test with this sample")
                : feed.transport === "push"
                  ? t("Test with this sample")
                  : t("Fetch once, apply nothing")}
            </Button>
            {test.isError && <p className={a.bad}>{(test.error as Error).message}</p>}
            {test.data && (
              <div data-testid="feed-test-results">
                {test.data.error && <p className={a.bad}>{test.data.error}</p>}
                {test.data.note && <p className={a.dim}>{test.data.note}</p>}
                {Object.entries(test.data.secrets).map(([k, v]) => (
                  <p key={k} className={a.dim}>
                    {v.name}: <span className={v.set ? a.ok : a.flag}>{v.set ? t("set") : t("not set")}</span>
                  </p>
                ))}
                {test.data.results.length === 0 && !test.data.error && (
                  <p className={a.dim}>{t("Nothing came back to read.")}</p>
                )}
                <ul className={f.results}>
                  {test.data.results.map((r) => (
                    <li key={r.index}>
                      <span className={f.outcome} data-o={r.outcome}>
                        {tc(OUTCOME_LABEL[r.outcome], "feed")}
                      </span>{" "}
                      {r.movement && <b>{r.movement} </b>}
                      {r.reason ?? (r.changed?.length ? r.changed.join(", ") : "")}
                      {r.ignored && r.ignored.length > 0 && (
                        <div className={a.dim}>
                          {t("Not read (ignored and reported):")} {r.ignored.join(", ")}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
